import { Checkbox, Radio, RadioGroup, Slider, cn } from "@nextui-org/react";
import { useEffect, useState } from "react";
import { isEmpty, isEqual } from "lodash";
import { IProduct } from "@/interfaces/product";
import SearchProduct from "@/components/Shares/SearchProduct";
import { formatPrice } from "@/utils/format";
import { ICategoryAttribute } from "@/interfaces/category";

interface SearchSideBarProps {
  filteredProducts: IProduct[];
  setFilteredProducts: (e: IProduct[]) => void;
  category?: string;
  setCategory?: (e: string) => void;
  products: IProduct[];
  omg?: boolean;
  cxt?: boolean;
  inStockFilter: boolean;
  setInStockFilter: (value: boolean) => void;
  saleFilter: boolean;
  setSaleFilter: (value: boolean) => void;
  bestSellerFilter: boolean;
  setBestSellerFilter: (value: boolean) => void;
  newArrivalFilter: boolean;
  setNewArrivalFilter: (value: boolean) => void;
  attributes?: Record<string, Record<string, number[]>>;
  selectedAttributeFilters?: {
    [attributeName: string]: Set<string>;
  };
  setSelectedAttributeFilters?: (value: any) => void;
}

interface ICu {
  name: string;
  quantity: number;
}

const SearchSideBar: React.FC<SearchSideBarProps> = (props) => {
  const {
    setCategory,
    category,
    products,
    setFilteredProducts,
    filteredProducts,
    omg = true,
    cxt = false,
    inStockFilter,
    setInStockFilter,
    saleFilter,
    setSaleFilter,
    bestSellerFilter,
    setBestSellerFilter,
    newArrivalFilter,
    setNewArrivalFilter,
    attributes,
    selectedAttributeFilters,
    setSelectedAttributeFilters,
  } = props;
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(Infinity);
  const [categories, setCategories] = useState<ICu[]>();
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(Infinity);

  const handleFilter = () => {
    const filteredProducts = products.filter((product) => {
      return product.productVariants.some((variant) => {
        return variant.price >= min && variant.price <= max;
      });
    });
    setFilteredProducts(filteredProducts);
  };

  const wtf = (products: IProduct[]) => {
    const categoryCount = new Map<string, number>();

    products.forEach((item) => {
      const { category } = item;
      if (categoryCount.has("All")) {
        categoryCount.set("All", categoryCount.get("All")! + 1);
      } else {
        categoryCount.set("All", 1);
      }

      if (categoryCount.has(category.name)) {
        categoryCount.set(category.name, categoryCount.get(category.name)! + 1);
      } else {
        categoryCount.set(category.name, 1);
      }
    });

    const result = Array.from(categoryCount, ([name, quantity]) => ({
      name,
      quantity,
    }));

    return result;
  };

  const getMinValue = (products: IProduct[]) => {
    return products.reduce((minValue, product) => {
      const productMin = product.productVariants.reduce(
        (variantMin, variant) => Math.min(variantMin, variant.price),
        Infinity,
      );
      return Math.min(minValue, productMin);
    }, Infinity);
  };

  const getMaxValue = (products: IProduct[]) => {
    return products.reduce((maxValue, product) => {
      const productMax = product.productVariants.reduce(
        (variantMax, variant) => Math.max(variantMax, variant.price),
        0,
      );
      return Math.max(maxValue, productMax);
    }, 0);
  };

  useEffect(() => {
    if (isEmpty(products)) {
      setMaxValue(0);
      setMinValue(0);
    } else {
      setMaxValue(getMaxValue(products));
      setMinValue(getMinValue(products));
    }
    omg && setCategories(wtf(products));
    setFilteredProducts(products);
  }, [products]);

  useEffect(() => {
    omg && setCategories(wtf(filteredProducts));
  }, [filteredProducts]);

  useEffect(() => {
    if (minValue > 0) {
      setMax(maxValue);
      setMin(minValue);
    }
  }, [maxValue, minValue, setMax, setMin]);

  return (
    <div className="flex flex-col gap-12">
      <div>
        <SearchProduct resultNumber={3} />
      </div>
      {/* Availablity */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Availability</h2>
        <Checkbox
          color="warning"
          radius="none"
          isSelected={inStockFilter}
          onChange={(e) => setInStockFilter(e.target.checked)}
        >
          In Stock
        </Checkbox>
      </div>
      {/* Price */}
      {minValue > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Price</h2>
          <Slider
            size="sm"
            minValue={minValue}
            maxValue={maxValue}
            value={[min, max]}
            aria-label="price filter"
            formatOptions={{ style: "currency", currency: "AUD" }}
            classNames={{
              base: "max-w-md gap-3",
              filler: "bg-icon",
            }}
            renderThumb={({ index, ...props }) => (
              <div
                {...props}
                className="group p-1 top-1/2 bg-background border-small border-default-200 shadow-medium rounded-full cursor-grab data-[dragging=true]:cursor-grabbing"
              >
                <span
                  className={cn(
                    "bg-icon shadow-small rounded-full size-4 block group-data-[dragging=true]:scale-80",
                  )}
                />
              </div>
            )}
            onChange={(e: number | number[]) => {
              if (Array.isArray(e)) {
                setMin(e[0]);
                setMax(e[1]);
              }
            }}
          />
          <div className="text-sm flex justify-between mt-3">
            <p className="">
              PRICE: {formatPrice(min)} - {formatPrice(max)}
            </p>
            <button
              className="text-icon cursor-pointer select-none"
              onClick={() => handleFilter()}
            >
              FILTER
            </button>
          </div>
        </div>
      )}
      {/* Category */}
      {setCategory && omg && categories && (
        <div>
          <h2 className="text-lg font-semibold">Category</h2>
          <RadioGroup
            className="mt-4"
            defaultValue={"All"}
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
            }}
            aria-label="Category filter"
          >
            {categories.map((category, index) => {
              const { name, quantity } = category;
              return (
                <Radio
                  color="warning"
                  key={index}
                  value={name}
                  className="hover:text-icon transition-all duration-500 cursor-pointer"
                >
                  {name} ({quantity})
                </Radio>
              );
            })}
          </RadioGroup>
        </div>
      )}

      {/* Feature */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Feature</h2>
        <div className="flex flex-col gap-2">
          <Checkbox
            color="warning"
            radius="none"
            isSelected={saleFilter}
            onChange={(e) => setSaleFilter(e.target.checked)}
          >
            Sale
          </Checkbox>
          <Checkbox
            color="warning"
            radius="none"
            isSelected={bestSellerFilter}
            onChange={(e) => setBestSellerFilter(e.target.checked)}
          >
            Best Seller
          </Checkbox>
          <Checkbox
            color="warning"
            radius="none"
            isSelected={newArrivalFilter}
            onChange={(e) => setNewArrivalFilter(e.target.checked)}
          >
            New Arrival
          </Checkbox>
        </div>
      </div>

      {cxt &&
        attributes &&
        selectedAttributeFilters &&
        setSelectedAttributeFilters &&
        Object.entries(attributes).map(([attributeName, valuesMap]) => {
          return (
            <div key={attributeName}>
              <h2 className="text-lg font-semibold mb-4">{attributeName}</h2>
              <div className="flex flex-col gap-2">
                {Object.entries(valuesMap).map(([value, productIds]) => {
                  if (isEmpty(value)) return null;
                  return (
                    <Checkbox
                      key={value}
                      color="warning"
                      radius="none"
                      isSelected={
                        selectedAttributeFilters[attributeName]?.has(value) ??
                        false
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedAttributeFilters((prev: any) => {
                          const currentSet = new Set(prev[attributeName] ?? []);
                          if (checked) {
                            currentSet.add(value);
                          } else {
                            currentSet.delete(value);
                          }
                          return {
                            ...prev,
                            [attributeName]: currentSet,
                          };
                        });
                      }}
                    >
                      {value} ({productIds.length})
                    </Checkbox>
                  );
                })}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default SearchSideBar;
